import { FC, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useLaunchDarkly } from '@/providers/launchdarkly-provider';
import { LoadingSpinner } from '@/components/loading-spinner';
import { useOrgNavigate } from '@/hooks/use-org-navigate';
import { useResumeLocation } from '@/hooks/use-resume-location';

const DEFAULT_HOME_ROUTE = '/workflow';

/**
 * Component that redirects to a route specified by the LaunchDarkly flag,
 * defaulting to /workflow if no flag is set.
 *
 * Every sign-in funnels through here, so it is also where a user signed out by
 * inactivity is returned to the page they were on instead of the landing page.
 */
export const ConditionalLandingRedirect: FC = () => {
  const navigate = useNavigate();
  const orgNavigate = useOrgNavigate();
  const { homeRedirectRoute, isLoading } = useLaunchDarkly();
  const resumeLocation = useResumeLocation();

  const redirectRoute = homeRedirectRoute?.trim() || DEFAULT_HOME_ROUTE;

  useEffect(() => {
    if (resumeLocation === undefined) return;

    // Not orgNavigate: a captured path from outside `/o/:orgId` would get a
    // second org prefix.
    if (resumeLocation) {
      navigate(resumeLocation, { replace: true });
      return;
    }

    if (!isLoading) {
      orgNavigate(redirectRoute, { replace: true });
    }
  }, [navigate, orgNavigate, redirectRoute, isLoading, resumeLocation]);

  if (isLoading) {
    return <LoadingSpinner fullScreen size='lg' />;
  }

  return null;
};
